/**
 * SubstrateBatchForm Component
 *
 * Multi-step dialog form for creating substrate batches.
 * Part of US-P2-003: Substrate Production & Block Creation
 * Enhanced for US-P6-002: Recipe Cost Integration (Step 10)
 *
 * Steps:
 * 1. Select recipe (with auto-selection based on last used for species)
 * 2. Select block format (with inventory display)
 * 3. Enter total_blocks (with material allocation preview)
 * 4. Notes & Summary (with cost breakdown)
 *
 * Material Integration:
 * - Shows FIFO allocation preview for each ingredient
 * - Displays estimated material cost per batch and per block
 * - Blocks creation if insufficient stock
 * - Records material usage on batch creation
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { toLocalDateString } from '@/utils/dateUtils'
import { useSubstrateMutations } from '@/hooks/useSubstrateBatches'
import { useRecipes, useBlockFormats } from '@/hooks/useSubstrateResources'
import { useSubstrateBatchWithMaterials } from '@/hooks/useSubstrateBatchWithMaterials'
import RecipeAllocationPreview from '@/components/inventory/RecipeAllocationPreview'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertTriangle,
  Package,
  ChevronRight,
  ChevronLeft,
  Scale,
  Boxes,
  CheckCircle2,
  Loader2,
  PoundSterling,
  Layers
} from 'lucide-react'

// Suggested block counts (multiples of 7 for trolley optimization)
const SUGGESTED_BLOCKS = [7, 14, 21, 28, 35, 42, 49, 54, 56, 63]

/**
 * Format a number as currency (GBP)
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export default function SubstrateBatchForm({
  isOpen,
  onClose,
  onSave,
  editingBatch = null
}) {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    recipe_id: '',
    block_format_id: '',
    total_blocks: 54,
    dry_weight_kg: null,
    mixed_at: toLocalDateString(new Date()),
    notes: ''
  })
  const [error, setError] = useState(null)
  const [selectedRecipe, setSelectedRecipe] = useState(null)
  const [selectedFormat, setSelectedFormat] = useState(null)

  // Multi-format fill state
  const [multiFormatMode, setMultiFormatMode] = useState(false)
  const [fills, setFills] = useState({}) // { formatId: quantity }

  // Material allocation state
  const [allocationStatus, setAllocationStatus] = useState(null)
  const [materialCost, setMaterialCost] = useState({ total: 0, perBlock: 0 })
  const [allIngredientsAvailable, setAllIngredientsAvailable] = useState(true)
  const [calculatedDryWeight, setCalculatedDryWeight] = useState(null)

  // Fetch recipes and block formats
  const { recipes, loading: recipesLoading } = useRecipes()
  const { blockFormats, loading: formatsLoading } = useBlockFormats()
  const { updateBatch, loading: updateSaving } = useSubstrateMutations()
  const {
    createBatchWithMaterials,
    isLoading: creatingWithMaterials,
    error: materialError
  } = useSubstrateBatchWithMaterials()

  const saving = updateSaving || creatingWithMaterials

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (editingBatch) {
        setFormData({
          recipe_id: editingBatch.recipe_id || '',
          block_format_id: editingBatch.block_format_id || '',
          total_blocks: editingBatch.total_blocks || 54,
          dry_weight_kg: editingBatch.dry_weight_kg || null,
          mixed_at: editingBatch.mixed_at?.split('T')[0] || toLocalDateString(new Date()),
          notes: editingBatch.notes || ''
        })
        // Find and set selected recipe/format
        const recipe = recipes.find(r => r.id === editingBatch.recipe_id)
        const format = blockFormats.find(f => f.id === editingBatch.block_format_id)
        setSelectedRecipe(recipe)
        setSelectedFormat(format)
        setStep(3) // Go to blocks step for editing
      } else {
        setFormData({
          recipe_id: '',
          block_format_id: '',
          total_blocks: 54,
          dry_weight_kg: null,
          mixed_at: toLocalDateString(new Date()),
          notes: ''
        })
        setSelectedRecipe(null)
        setSelectedFormat(null)
        setStep(1)
      }
      setError(null)
      setMultiFormatMode(false)
      setFills({})
      setAllocationStatus(null)
      setMaterialCost({ total: 0, perBlock: 0 })
      setAllIngredientsAvailable(true)
      setCalculatedDryWeight(null)
    }
  }, [isOpen, editingBatch, recipes, blockFormats])

  // Calculate scaled ingredients based on recipe and batch size using ratio-based formula
  // ingredient_kg = (ingredient.parts / total_parts) × (blocks × block_weight_kg)
  const scaledIngredients = useMemo(() => {
    if (!selectedRecipe || !selectedRecipe.ingredients) return []
    if (!multiFormatMode && !selectedFormat) return []

    const totalBatchKg = multiFormatMode ? totalWeight : formData.total_blocks * (selectedFormat.weight_kg || 4.5)
    const totalParts = selectedRecipe.ingredients.reduce(
      (sum, ing) => sum + (ing.parts ?? ing.quantity_kg ?? 0), 0
    )

    if (totalParts <= 0) return []

    return selectedRecipe.ingredients.map(ing => {
      const parts = ing.parts ?? ing.quantity_kg ?? 0
      const scaledQty = (parts / totalParts) * totalBatchKg
      return {
        ...ing,
        parts,
        scaled_quantity: Math.round(scaledQty * 100) / 100
      }
    })
  }, [selectedRecipe, selectedFormat, formData.total_blocks])

  // Calculate total substrate weight (handles both single + multi-format)
  const totalWeight = useMemo(() => {
    if (multiFormatMode) {
      // Sum weight across all fill entries
      const activeFormats = blockFormats.filter(f => f.active !== false)
      return Object.entries(fills).reduce((sum, [formatId, qty]) => {
        const format = activeFormats.find(f => f.id === formatId)
        return sum + (parseInt(qty) || 0) * (format?.weight_kg || 0)
      }, 0)
    }
    if (!selectedFormat) return 0
    return formData.total_blocks * selectedFormat.weight_kg
  }, [selectedFormat, formData.total_blocks, multiFormatMode, fills, blockFormats])

  // Dry weight calculated from actual moisture content of ingredients
  // Falls back to 32% estimate if not available from Edge Function
  const estimatedDryWeight = useMemo(() => {
    // Prefer calculated dry weight from Edge Function (uses actual moisture content)
    if (calculatedDryWeight != null && calculatedDryWeight > 0) {
      return Math.round(calculatedDryWeight * 10) / 10
    }
    // Fallback: 32% dry matter content - typical for masters mix and sawdust substrates
    if (!totalWeight) return 0
    return Math.round(totalWeight * 0.32 * 10) / 10
  }, [calculatedDryWeight, totalWeight])

  // Handle allocation status changes from RecipeAllocationPreview
  const handleAllocationChange = useCallback((status) => {
    if (status) {
      setAllocationStatus(status)
      setAllIngredientsAvailable(status.allIngredientsAvailable)
      setMaterialCost({
        total: status.totalCost || 0,
        perBlock: status.costPerBlock || 0
      })
      // Use calculated dry weight from Edge Function (based on moisture content)
      if (status.totalDryWeightKg != null) {
        setCalculatedDryWeight(status.totalDryWeightKg)
      }
    }
  }, [])

  const handleRecipeSelect = (recipeId) => {
    const recipe = recipes.find(r => r.id === recipeId)
    setSelectedRecipe(recipe)
    setFormData(prev => ({
      ...prev,
      recipe_id: recipeId,
      total_blocks: recipe?.blocks_per_batch || 54
    }))
    setError(null)
    // Reset allocation status when recipe changes
    setAllocationStatus(null)
    setCalculatedDryWeight(null)
  }

  const handleFormatSelect = (formatId) => {
    const format = blockFormats.find(f => f.id === formatId)
    setSelectedFormat(format)
    setFormData(prev => ({
      ...prev,
      block_format_id: formatId
    }))
    setError(null)
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  // Handle fill quantity changes in multi-format mode
  const handleFillChange = (formatId, value) => {
    const qty = parseInt(value) || 0
    setFills(prev => {
      const next = { ...prev }
      if (qty > 0) {
        next[formatId] = qty
      } else {
        delete next[formatId]
      }
      return next
    })
  }

  // Auto-sync total_blocks from fills
  const fillsTotal = useMemo(() => {
    return Object.values(fills).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0)
  }, [fills])

  // Keep formData.total_blocks in sync with fills sum
  useEffect(() => {
    if (multiFormatMode && fillsTotal > 0) {
      setFormData(prev => ({ ...prev, total_blocks: fillsTotal }))
    }
  }, [multiFormatMode, fillsTotal])

  // Toggle multi-format mode
  const handleMultiFormatToggle = (checked) => {
    setMultiFormatMode(checked)
    if (!checked) {
      setFills({})
      // Reset total_blocks if switching back to single mode
      setFormData(prev => ({
        ...prev,
        total_blocks: selectedRecipe?.blocks_per_batch || 54
      }))
    } else {
      // Pre-set the first active format as block_format_id for the batch record
      const activeFormats = blockFormats.filter(f => f.active !== false)
      if (activeFormats.length > 0 && !formData.block_format_id) {
        setFormData(prev => ({
          ...prev,
          block_format_id: activeFormats[0].id
        }))
        setSelectedFormat(activeFormats[0])
      }
    }
  }

  const handleNext = () => {
    // Validate current step
    if (step === 1 && !formData.recipe_id) {
      setError('Please select a recipe')
      return
    }
    if (step === 2) {
      if (multiFormatMode) {
        if (fillsTotal < 1) {
          setError('Please enter quantities for at least one format')
          return
        }
      } else {
        if (!formData.block_format_id) {
          setError('Please select a block format')
          return
        }
      }
    }
    if (step === 3) {
      if (!formData.total_blocks || formData.total_blocks < 1) {
        setError('Please enter at least 1 block')
        return
      }
      // Check material availability before proceeding to summary
      if (!allIngredientsAvailable) {
        setError('Cannot proceed: some materials have insufficient stock')
        return
      }
    }

    setError(null)
    setStep(prev => prev + 1)
  }

  const handleBack = () => {
    setError(null)
    setStep(prev => prev - 1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    try {
      if (editingBatch) {
        // For editing, use the standard update (no material changes)
        await updateBatch({
          id: editingBatch.id,
          ...formData,
          total_blocks: parseInt(formData.total_blocks)
        })
      } else {
        // For new batches, use the integrated creation with material allocation
        const batchData = {
          ...formData,
          total_blocks: parseInt(formData.total_blocks),
          block_weight_kg: selectedFormat?.weight_kg || 4.5
        }

        // Add multi-format fills if enabled
        if (multiFormatMode && Object.keys(fills).length > 0) {
          batchData.fills = Object.entries(fills)
            .filter(([, qty]) => parseInt(qty) > 0)
            .map(([formatId, qty]) => ({
              block_format_id: formatId,
              quantity: parseInt(qty)
            }))
          // In multi-format mode, use weighted average for block_weight_kg
          const activeFormats = blockFormats.filter(f => f.active !== false)
          const totalQty = batchData.fills.reduce((s, f) => s + f.quantity, 0)
          if (totalQty > 0) {
            batchData.block_weight_kg = batchData.fills.reduce((sum, f) => {
              const fmt = activeFormats.find(bf => bf.id === f.block_format_id)
              return sum + f.quantity * (fmt?.weight_kg || 4.5)
            }, 0) / totalQty
          }
        }

        await createBatchWithMaterials(batchData)
      }
      onSave?.()
    } catch (err) {
      setError(err.message || materialError || 'Failed to save substrate batch')
    }
  }

  const isLastStep = step === 4
  const canProceed = (step === 1 && formData.recipe_id) ||
                     (step === 2 && (multiFormatMode ? fillsTotal > 0 : !!formData.block_format_id)) ||
                     (step === 3 && formData.total_blocks >= 1 && allIngredientsAvailable) ||
                     step === 4

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {editingBatch ? 'Edit Substrate Batch' : 'Create Substrate Batch'}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 py-2">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={`flex items-center ${s < 4 ? 'flex-1' : ''}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step === s ? 'bg-odin-blue text-white' :
                    step > s ? 'bg-odin-green text-white' :
                    'bg-surface-secondary text-content-tertiary'}`}
              >
                {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-1 mx-2
                    ${step > s ? 'bg-odin-green' : 'bg-surface-secondary'}`}
                />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Select Recipe */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-sm text-content-secondary mb-2">
                Select the substrate recipe for this batch
              </div>
              <div className="grid gap-3 max-h-[400px] overflow-y-auto">
                {recipesLoading ? (
                  <div className="text-center py-8 text-content-secondary">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading recipes...
                  </div>
                ) : recipes.length === 0 ? (
                  <div className="text-center py-8 text-content-secondary">
                    No recipes found. Please create a recipe first.
                  </div>
                ) : (
                  recipes.filter(r => r.active !== false).map(recipe => (
                    <Card
                      key={recipe.id}
                      className={`cursor-pointer transition-all ${
                        formData.recipe_id === recipe.id
                          ? 'border-odin-blue bg-odin-blue/5'
                          : 'hover:border-content-tertiary'
                      }`}
                      onClick={() => handleRecipeSelect(recipe.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{recipe.name}</div>
                            <div className="text-sm text-content-secondary">
                              {recipe.blocks_per_batch} blocks per batch
                            </div>
                            {recipe.compatible_species?.length > 0 && (
                              <div className="flex gap-1 mt-2">
                                {recipe.compatible_species.map(species => (
                                  <Badge key={species} variant="outline" className="text-xs">
                                    {species}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          {formData.recipe_id === recipe.id && (
                            <CheckCircle2 className="h-5 w-5 text-odin-blue" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Block Format */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-content-secondary">
                  {multiFormatMode
                    ? 'Enter quantities for each bag size'
                    : 'Select the block bag format to use'}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={multiFormatMode}
                    onCheckedChange={handleMultiFormatToggle}
                  />
                  <span className="text-sm flex items-center gap-1">
                    <Layers className="h-3.5 w-3.5" />
                    Multiple bag sizes
                  </span>
                </label>
              </div>

              {formatsLoading ? (
                <div className="text-center py-8 text-content-secondary">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading formats...
                </div>
              ) : blockFormats.length === 0 ? (
                <div className="text-center py-8 text-content-secondary">
                  No block formats found. Please create one first.
                </div>
              ) : multiFormatMode ? (
                /* Multi-format mode: list with quantity inputs */
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {blockFormats.filter(f => f.active !== false).map(format => (
                    <Card key={format.id} className="border">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="font-medium">{format.name}</div>
                            <div className="text-sm text-content-secondary">
                              {format.weight_kg} kg per block
                              {format.supplier && ` - ${format.supplier}`}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm">
                              <span className="flex items-center gap-1 text-content-tertiary">
                                <Boxes className="h-3.5 w-3.5" />
                                Stock: {format.current_inventory || 0}
                              </span>
                            </div>
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min={0}
                              max={500}
                              value={fills[format.id] || ''}
                              onChange={(e) => handleFillChange(format.id, e.target.value)}
                              placeholder="0"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Fill summary */}
                  {fillsTotal > 0 && (
                    <Card className="bg-surface-secondary border-0">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-content-secondary">Total blocks:</span>
                          <span className="font-bold">{fillsTotal}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                          <span className="text-content-secondary">Total weight:</span>
                          <span className="font-bold">{totalWeight.toFixed(1)} kg</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                /* Single-format mode: card selection */
                <div className="grid gap-3 max-h-[400px] overflow-y-auto">
                  {blockFormats.filter(f => f.active !== false).map(format => (
                    <Card
                      key={format.id}
                      className={`cursor-pointer transition-all ${
                        formData.block_format_id === format.id
                          ? 'border-odin-blue bg-odin-blue/5'
                          : 'hover:border-content-tertiary'
                      }`}
                      onClick={() => handleFormatSelect(format.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{format.name}</div>
                            <div className="text-sm text-content-secondary">
                              {format.weight_kg} kg per block
                              {format.supplier && ` - ${format.supplier}`}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span className="flex items-center gap-1">
                                <Boxes className="h-4 w-4 text-content-tertiary" />
                                Stock: {format.current_inventory || 0}
                              </span>
                              {format.min_stock_level && format.current_inventory < format.min_stock_level && (
                                <Badge variant="destructive" className="text-xs">
                                  Low Stock
                                </Badge>
                              )}
                            </div>
                          </div>
                          {formData.block_format_id === format.id && (
                            <CheckCircle2 className="h-5 w-5 text-odin-blue" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Enter Block Count + Material Allocation Preview */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-sm text-content-secondary mb-2">
                Configure batch size and review material allocation
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {/* Left column: Block count and date */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="total_blocks">Number of Blocks</Label>
                    <Input
                      id="total_blocks"
                      type="number"
                      min={1}
                      max={500}
                      value={formData.total_blocks}
                      onChange={(e) => handleChange('total_blocks', e.target.value)}
                      className="text-lg"
                    />
                  </div>

                  {/* Suggested amounts */}
                  <div className="space-y-2">
                    <Label className="text-content-secondary text-xs">Quick Select</Label>
                    <div className="flex flex-wrap gap-1">
                      {SUGGESTED_BLOCKS.map(count => (
                        <Button
                          key={count}
                          type="button"
                          variant={formData.total_blocks == count ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleChange('total_blocks', count)}
                        >
                          {count}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Total weight calculation */}
                  {selectedFormat && (
                    <Card className="bg-surface-secondary border-0">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Scale className="h-4 w-4 text-content-tertiary" />
                            <span className="text-sm text-content-secondary">Total Weight (wet):</span>
                          </div>
                          <span className="text-lg font-bold">
                            {totalWeight.toFixed(1)} kg
                          </span>
                        </div>

                        {/* Dry weight for BE calculation */}
                        <div className="pt-2 border-t border-border">
                          <Label htmlFor="dry_weight_kg" className="text-xs text-content-secondary">
                            Dry Weight (for BE calculation)
                          </Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              id="dry_weight_kg"
                              type="number"
                              min={0}
                              max={totalWeight}
                              step={0.1}
                              value={formData.dry_weight_kg ?? ''}
                              onChange={(e) => handleChange('dry_weight_kg', e.target.value ? parseFloat(e.target.value) : null)}
                              placeholder={estimatedDryWeight.toFixed(1)}
                              className="w-24 text-sm"
                            />
                            <span className="text-xs text-content-tertiary">kg</span>
                            {!formData.dry_weight_kg && estimatedDryWeight > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleChange('dry_weight_kg', estimatedDryWeight)}
                              >
                                Use estimate
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-content-tertiary mt-1">
                            {calculatedDryWeight != null && calculatedDryWeight > 0
                              ? `Calculated: ${estimatedDryWeight.toFixed(1)} kg (from ingredient moisture content)`
                              : `Estimated: ~${estimatedDryWeight.toFixed(1)} kg (32% of wet weight)`}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Mixing date */}
                  <div className="space-y-2">
                    <Label htmlFor="mixed_at">Mixing Date</Label>
                    <Input
                      id="mixed_at"
                      type="date"
                      value={formData.mixed_at}
                      onChange={(e) => handleChange('mixed_at', e.target.value)}
                    />
                  </div>
                </div>

                {/* Right column: Material Allocation Preview */}
                <div>
                  {formData.recipe_id && formData.total_blocks > 0 && (selectedFormat || multiFormatMode) && (
                    <RecipeAllocationPreview
                      recipeId={formData.recipe_id}
                      totalBlocks={parseInt(formData.total_blocks) || 0}
                      blockWeightKg={
                        multiFormatMode && fillsTotal > 0
                          ? totalWeight / fillsTotal
                          : (selectedFormat?.weight_kg || 4.5)
                      }
                      onAllocationChange={handleAllocationChange}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Notes & Summary */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="text-sm text-content-secondary mb-2">
                Review and add any notes
              </div>

              {/* Summary */}
              <Card className="bg-surface-secondary border-0">
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-content-secondary">Recipe:</span>
                    <span className="font-medium">{selectedRecipe?.name}</span>
                  </div>
                  {multiFormatMode ? (
                    <>
                      <div className="text-content-secondary text-sm font-medium">Fills:</div>
                      {Object.entries(fills)
                        .filter(([, qty]) => parseInt(qty) > 0)
                        .map(([formatId, qty]) => {
                          const fmt = blockFormats.find(f => f.id === formatId)
                          return (
                            <div key={formatId} className="flex justify-between pl-2">
                              <span className="text-content-secondary">{fmt?.name}:</span>
                              <span className="font-medium">{qty} blocks ({(parseInt(qty) * (fmt?.weight_kg || 0)).toFixed(1)} kg)</span>
                            </div>
                          )
                        })}
                      <div className="flex justify-between border-t pt-1">
                        <span className="text-content-secondary">Total Blocks:</span>
                        <span className="font-medium">{formData.total_blocks}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-content-secondary">Block Format:</span>
                        <span className="font-medium">{selectedFormat?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-content-secondary">Total Blocks:</span>
                        <span className="font-medium">{formData.total_blocks}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-content-secondary">Total Weight (wet):</span>
                    <span className="font-medium">{totalWeight.toFixed(1)} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-content-secondary">Dry Weight:</span>
                    <span className="font-medium">
                      {formData.dry_weight_kg
                        ? `${formData.dry_weight_kg.toFixed(1)} kg`
                        : `~${estimatedDryWeight.toFixed(1)} kg (est.)`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-content-secondary">Mixing Date:</span>
                    <span className="font-medium">
                      {new Date(formData.mixed_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Material Cost Summary */}
              {!editingBatch && materialCost.total > 0 && (
                <Card className="bg-green-50/50 border-green-200">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-green-800">
                      <PoundSterling className="h-4 w-4" />
                      Estimated Material Cost
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-content-secondary">Total:</span>
                      <span className="font-bold text-lg">{formatCurrency(materialCost.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-content-tertiary">
                      <span>Per Block:</span>
                      <span className="font-mono">{formatCurrency(materialCost.perBlock)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any observations or special handling notes..."
                  rows={3}
                />
              </div>

              {/* Material usage notice for new batches */}
              {!editingBatch && (
                <Alert className="bg-blue-50/50 border-blue-200">
                  <Package className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Material usage will be recorded and inventory will be deducted when this batch is created.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={step === 1 ? onClose : handleBack}
              disabled={saving}
            >
              {step === 1 ? 'Cancel' : (
                <>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </>
              )}
            </Button>

            {isLastStep ? (
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {editingBatch ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingBatch ? 'Update Batch' : 'Create Batch'
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                disabled={!canProceed}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
